import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Outlet,
  useNavigate,
} from "react-router-dom";

/* ========== COMPONENTS ========== */
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsappChat from "./components/WhatsappChat";

/* ========== PUBLIC PAGES ========== */
import Home from "./pages/Home";
import ContactUs from "./pages/ContactUs";
import BookSession from "./pages/BookSession";

import Events from "./pages/Events";
import AboutUs from "./pages/AboutUs";
import Careers from "./pages/Careers";
import ProgrammeCategories from "./pages/ProgrammeCategories";
import LeadershipGrowthMastery from "./pages/LeadershipGrowthMastery";
import AdvanceGrowthMastery from "./pages/AdvanceGrowthMastery";
import EliteGrowthMastery from "./pages/EliteGrowthMastery";
import SpecialGrowthMastery from "./pages/SpecialGrowthMastery";
import LegacyGrowthMastery from "./pages/LegacyGrowthMastery";
import DailyScientificVisualization from "./pages/DailyScientificVisualization";
import LimitlessMindRewiring from "./pages/LimitlessMindRewiring";
import StudentDreamMastery from "./pages/StudentDreamMastery";
import IdentityTransformationLab from "./pages/IdentityTransformationLab";
import AdvanceLeadershipTransformationMastery from "./pages/AdvanceLeadershipTransformationMastery";
import LeadershipLegacyProgram from "./pages/LeadershipLegacyProgram";
import CorporateTraining from "./pages/CorporateTraining";
import Counselling from "./pages/Counselling";
import PaymentStatus from "./pages/PaymentStatus";
import RefundPolicy from "./pages/RefundPolicy";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";


/* ========== AUTH PAGES ========== */
import AdminLogin from "./pages/AdminLogin";
import CustomerLogin from "./pages/CustomerLogin";

/* ========== DASHBOARDS ========== */
import AdminDashboard from "./pages/AdminDashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import AdminBooking from "./pages/AdminBooking";
import AdminSlots from "./pages/AdminSlots";
import AdminContactMessages from "./pages/AdminContactMessages";

/* ========== ROUTE GUARDS ========== */
import AdminRoute from "./routes/AdminRoute";
import CustomerRoute from "./routes/CustomerRoute";

import { Settings, LogOut } from "lucide-react";

/* ================= SCROLL TO TOP ================= */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

/* ================= PUBLIC LAYOUT ================= */
const PublicLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-grow">
      <Outlet />
    </main>
    <WhatsappChat />
    <Footer />
  </div>
);

/* ================= ADMIN LAYOUT ================= */
const AdminLayout = () => {
  const navigate = useNavigate();

  /* --- AUTO LOGOUT (10 MINS) --- */
  const logout = React.useCallback(() => {
    localStorage.removeItem("adminToken");
    navigate("/adminlogin");
  }, [navigate]);

  useEffect(() => {
    const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 Minutes
    let timer;

    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
      }, INACTIVITY_LIMIT);
    };

    // Events to track activity
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) => window.addEventListener(event, resetTimer));

    // Initial Start
    resetTimer();

    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [logout]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <div className="bg-white border-b shadow sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold uppercase">
            <Settings className="w-5 h-5 text-exiser-primary" />
            Exiser Admin
          </div>

          <button
            onClick={logout}
            className="flex items-center gap-2 text-red-600 font-semibold"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

/* ================= APP ================= */
const App = () => {
  return (
    <Router>
      <ScrollToTop />

      <Routes>
        {/* ===== PUBLIC ROUTES ===== */}
        <Route element={<PublicLayout />}>
          <Route index element={<Home />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/book" element={<BookSession />} />

          <Route path="/events" element={<Events />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/categories" element={<ProgrammeCategories />} />
          <Route path="/corporate-training" element={<CorporateTraining />} />
          <Route path="/counselling" element={<Counselling />} />
          <Route path="/success" element={<PaymentStatus />} />
          <Route path="/failure" element={<PaymentStatus />} />
          <Route path="/leadership-growth-mastery" element={<LeadershipGrowthMastery />} />
          <Route path="/advance-growth-mastery" element={<AdvanceGrowthMastery />} />
          <Route path="/elite-growth-mastery" element={<EliteGrowthMastery />} />
          <Route path="/special-growth-mastery" element={<SpecialGrowthMastery />} />
          <Route path="/legacy-growth-mastery" element={<LegacyGrowthMastery />} />
          <Route path="/daily-scientific-visualization" element={<DailyScientificVisualization />} />
          <Route path="/limitless-mind-rewiring" element={<LimitlessMindRewiring />} />
          <Route path="/student-dream-mastery" element={<StudentDreamMastery />} />
          <Route path="/identity-transformation-lab" element={<IdentityTransformationLab />} />
          <Route path="/advance-leadership-transformation-mastery" element={<AdvanceLeadershipTransformationMastery />} />
          <Route path="/leadership-legacy-program" element={<LeadershipLegacyProgram />} />
          <Route path="/adminlogin" element={<AdminLogin />} />
          <Route path="/customer-login" element={<CustomerLogin />} />
          <Route path="/refund_policy" element={<RefundPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy_policy" element={<PrivacyPolicy />} />
        </Route>

        {/* ===== CUSTOMER DASHBOARD (PROTECTED) ===== */}
        <Route
          path="/customer-dashboard"
          element={
            <CustomerRoute>
              <CustomerDashboard />
            </CustomerRoute>
          }
        />

        {/* ===== ADMIN ROUTES ===== */}
        <Route element={<AdminLayout />}>
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <AdminRoute>
                <AdminBooking filterType="all" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/one-to-one"
            element={
              <AdminRoute>
                <AdminBooking filterType="1:1" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/course-enrollments"
            element={
              <AdminRoute>
                <AdminBooking filterType="program" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/slots"
            element={
              <AdminRoute>
                <AdminSlots />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/contact-messages"
            element={
              <AdminRoute>
                <AdminContactMessages filterType="general" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/corporate-proposals"
            element={
              <AdminRoute>
                <AdminContactMessages filterType="corporate" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/group-proposals"
            element={
              <AdminRoute>
                <AdminContactMessages filterType="group" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/legacy-proposals"
            element={
              <AdminRoute>
                <AdminContactMessages filterType="legacy" />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/leadership-legacy-proposals"
            element={
              <AdminRoute>
                <AdminContactMessages filterType="leadership-legacy" />
              </AdminRoute>
            }
          />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;